/**
 * scheduleReturnTo.ts — deep-link origin breadcrumb for Scenario Planning.
 *
 * The quote editor and the work-order page navigate into Scenario Planning
 * with `state: { scheduleReturnTo: { path, label } }`. History `state` is
 * untyped at runtime (it survives reloads/back-forward and could be anything),
 * so this helper validates the shape defensively instead of casting blindly.
 *
 * Pure function — unit-tested in scheduleReturnTo.test.ts.
 */

import type { ScheduleReturnTo } from '../types/schedule';

/**
 * Reads a ScheduleReturnTo out of a react-router `location.state`.
 * Returns null unless the state carries a well-formed `scheduleReturnTo`:
 *  - `path` a non-empty string, in-app relative ("/..."), and NOT
 *    protocol-relative ("//host") — history state is user-influenceable,
 *    so never navigate to anything but a same-app path;
 *  - `label` a non-empty string.
 */
export function readScheduleReturnTo(state: unknown): ScheduleReturnTo | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { scheduleReturnTo?: unknown }).scheduleReturnTo;
  if (!raw || typeof raw !== 'object') return null;
  const { path, label } = raw as { path?: unknown; label?: unknown };
  if (typeof path !== 'string' || path.length === 0) return null;
  if (typeof label !== 'string' || label.trim().length === 0) return null;
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  return { path, label };
}
